import { useState } from "react"
import { CalendarDays, X } from "lucide-react"
import { Button } from "@ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover"
import { Calendar as CalendarComponent } from "@ui/calendar"
import { formatPropertyName } from "@/lib/properties"

interface FilterProperty {
    name: string
    field: string
    type: 'string' | 'date'
}

interface DateFilterProps {
    property: FilterProperty
    value: Date | undefined
    onDateChange: (date: Date | undefined) => void
    onClear: () => void
}
export function DateFilter({ property, value, onDateChange, onClear }: Readonly<DateFilterProps>) {
    const [isOpen, setIsOpen] = useState(false)

    const handleDateSelect = (date: Date | undefined) => {
        onDateChange(date)
        setIsOpen(false)
    }

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation()
        onClear()
        setIsOpen(false)
    }

    const formatDateDisplay = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        })
    }

    const hasValue = value !== undefined && value !== null

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <div className="flex border flex-row gap-2 items-center justify-between cursor-pointer hover:bg-gray-200 rounded-md h-10 px-3 bg-white w-full">
                    <div className="flex items-center gap-2 flex-1 min-w-0 rounded-md">
                        <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-light text-left text-primary truncate">
                            { hasValue ? formatDateDisplay(value) : `Select ${formatPropertyName(property.name)}` }
                        </span>
                    </div>
                    {
                        hasValue && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 hover:bg-gray-100 shrink-0"
                                onClick={handleClear}
                                type="button"
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        )
                    }
                </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 z-60" align="start">
                <CalendarComponent
                    mode="single"
                    selected={value}
                    onSelect={handleDateSelect}
                />
            </PopoverContent>
        </Popover>
    )
}